import type { Workspace } from "@/lib/api";
import { readCanonicalTagValue } from "@/lib/profileMagicTagKeys";
import { upsertWorkspaceTagByKey } from "@/lib/workspaceSalesConfig";

export const WORKSPACE_SHOP_PINS_TAG_KEY = "shop_frequent_pins";

export type ShopPinId = "transactions" | "receivable" | "sheets" | "config";

export type ActiveShopPins = Record<ShopPinId, boolean>;

export const SHOP_PIN_IDS: ShopPinId[] = [
  "transactions",
  "receivable",
  "sheets",
  "config",
];

export const DEFAULT_ACTIVE_SHOP_PINS: ActiveShopPins = {
  transactions: true,
  receivable: true,
  sheets: true,
  config: true,
};

function normalizeTags(
  workspace?: Workspace | null,
): Array<{ key: string; value: string }> {
  return (workspace?.tags ?? [])
    .map((tag) => ({
      key: String(tag.key ?? "").trim(),
      value: String(tag.value ?? "").trim(),
    }))
    .filter((tag) => tag.key && tag.value);
}

export function resolveActiveShopPins(
  workspace?: Workspace | null,
): ActiveShopPins {
  const tags = normalizeTags(workspace);
  const raw = readCanonicalTagValue(tags, WORKSPACE_SHOP_PINS_TAG_KEY);
  if (!raw) return { ...DEFAULT_ACTIVE_SHOP_PINS };

  try {
    const parsed = JSON.parse(raw) as Partial<ActiveShopPins>;
    if (!parsed || typeof parsed !== "object") {
      return { ...DEFAULT_ACTIVE_SHOP_PINS };
    }

    const result = { ...DEFAULT_ACTIVE_SHOP_PINS };
    for (const id of SHOP_PIN_IDS) {
      if (typeof parsed[id] === "boolean") {
        result[id] = parsed[id];
      }
    }
    return result;
  } catch {
    return { ...DEFAULT_ACTIVE_SHOP_PINS };
  }
}

export async function saveWorkspaceShopPinsConfig(
  workspaceId: string,
  pins: ActiveShopPins,
  originalTags?: Workspace["tags"] | null,
): Promise<void> {
  await upsertWorkspaceTagByKey(
    workspaceId,
    WORKSPACE_SHOP_PINS_TAG_KEY,
    JSON.stringify(pins),
    originalTags,
  );
}
